// Copyright ©️ 2026 eVolve MEP, LLC

import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import {
  type WorkOrderPaginated,
  type StrongExtract,
  type Project,
  type Facility,
  useEvolvePaginatedGet,
} from '@evolvemep/api-ts';
import { Box, Card, Divider, Flex, Stack, Text } from '@mantine/core';

import { useRouteSearchParam } from 'app/useRouteSearchParam';
import { DraggableDivider } from 'components/Mantine/DraggableDivider';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { useWorkOrderStatuses } from 'hooks/typeCacheHooks/useWorkOrderStatuses';
import { useMutableSize } from 'hooks/useMutableSize';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { FabricationItems } from 'modules/Shop/EvolveFabrication/FabricationItems';
import {
  type WorkOrderFiltersState,
  WorkOrderFabricationFilters,
} from 'modules/Shop/EvolveFabrication/WorkOrderFabricationFilters';
import { WorkOrderSelector } from 'modules/Shop/EvolveFabrication/WorkOrderSelector';
import { NewWorkOrderModal } from 'modules/Shop/WorkOrders/WorkOrdersPage/NewWorkOrderModal';

type Props = {
  defaultWorkOrder?: WorkOrderPaginated | null;
  selectedProject: Project;
  selectedFacilities: Facility[];
  /** Bump this value to refresh the selected work order's fabrication items in place. */
  refreshTrigger: number;
  /** Called after the selected work order's fabrication parts are reassigned to the default machine profiles. */
  onMachineProfilesReassigned: () => void;
};

export const SELECTED_WORK_ORDER_QUERY_PARAM_KEY: StrongExtract<
  // Typed as such to ensure an error is thrown if the name of the key changes
  keyof WorkOrderPaginated,
  'workOrderId'
> = 'workOrderId';

export const FabricationWorkOrders = ({
  defaultWorkOrder,
  selectedFacilities: facilities,
  selectedProject,
  refreshTrigger,
  onMachineProfilesReassigned,
}: Props) => {
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrderPaginated | null>(defaultWorkOrder ?? null);
  const [, setFabricationListIdFromParams] = useRouteSearchParam('/fabrication', 'workOrderId');

  const onWorkOrderSelected = useCallback(
    (
      workOrder: WorkOrderPaginated | null,
      // If true, will re-select the Work Order even if it's already selected
      forceRefresh = false,
    ) => {
      setSelectedWorkOrder((wo) =>
        wo?.workOrderId === workOrder?.workOrderId && !forceRefresh
          ? wo
          : // First set to null, then set the new work order on a delay
            // so that the parts list is forced to refresh.
            null,
      );
      setFabricationListIdFromParams((v) => (v === workOrder?.workOrderId ? v : null));
      if (workOrder) {
        setTimeout(() => {
          setSelectedWorkOrder(workOrder);
          setFabricationListIdFromParams(workOrder.workOrderId);
        });
      }
    },
    [setFabricationListIdFromParams],
  );

  useEffect(() => {
    if (
      isNotNil(selectedWorkOrder) &&
      (selectedProject.projectId !== selectedWorkOrder.projectId ||
        (facilities.length > 0 && !facilities.some((f) => f.facilityId === selectedWorkOrder.facilityId)))
    ) {
      onWorkOrderSelected(null);
    }
  }, [facilities, onWorkOrderSelected, selectedWorkOrder, selectedProject]);

  const { states } = useWorkOrderStatuses();

  const [workOrderFilters, setWorkOrderFilters] = useState<WorkOrderFiltersState>({
    searchPhrase: '',
    includeCompleted: false,
  });

  const defaultOptsWorkOrders = useMemo(
    () => ({
      defaultConfig: {
        params: {
          projectIds: selectedProject.projectId,
          facilityIds: facilities.map((f) => f.facilityId),
          searchPhrase: encodeURIComponent(workOrderFilters.searchPhrase),
          workOrderStateIds: workOrderFilters.includeCompleted
            ? undefined
            : states?.filter((s) => s.name !== 'Completed').map((s) => s.stateId),
        },
      },
    }),
    [workOrderFilters, facilities, selectedProject.projectId, states],
  );

  const {
    data: workOrders,
    loading,
    fetchNextPage,
    setDefaultOpts: setDefaultOptsWorkOrders,
    reset: refreshWorkOrders,
  } = useEvolvePaginatedGet('shop/workOrder', defaultOptsWorkOrders);

  useEffect(() => {
    setDefaultOptsWorkOrders(defaultOptsWorkOrders);
  }, [defaultOptsWorkOrders, setDefaultOptsWorkOrders]);

  useEffect(() => {
    if (
      !loading &&
      isNotNil(selectedWorkOrder?.workOrderId) &&
      !workOrders.some((wo) => wo.workOrderId === selectedWorkOrder.workOrderId)
    ) {
      // If the selected work order is no longer in the list, pull the next page to refresh.
      void fetchNextPage();
    }
  }, [workOrders, fetchNextPage, loading, selectedWorkOrder?.workOrderId]);

  const [panel, setPanelWidth] = useMutableSize<HTMLDivElement>({
    defaultSize: 450,
    min: 400,
    max: 600,
  });

  return (
    <Card
      flex={1}
      withBorder
      p={0}
      style={{
        flexDirection: 'row',
        overflow: 'hidden',
      }}
    >
      <Stack ref={panel} gap={0} style={{ overflow: 'hidden' }}>
        <Flex gap="xs" p="md">
          <WorkOrderFabricationFilters
            filters={workOrderFilters}
            onChange={(f) => {
              setWorkOrderFilters(f);
              if (selectedWorkOrder?.workOrderStateName === 'Completed' && !f.includeCompleted) {
                onWorkOrderSelected(null);
              }
            }}
          />
          <NewWorkOrderModal
            defaultProject={selectedProject}
            disableProjectSelection
            onWorkOrderCreated={(workOrder) => {
              refreshWorkOrders();
              onWorkOrderSelected(workOrder);
            }}
          />
        </Flex>

        <Divider />

        <Stack flex={1} style={{ overflowY: 'auto' }}>
          <Stack gap={0}>
            {workOrders.map((workOrder) => (
              <Fragment key={workOrder.workOrderId}>
                <WorkOrderSelector
                  workOrder={workOrder}
                  onWorkOrderSelected={onWorkOrderSelected}
                  selected={selectedWorkOrder?.workOrderId === workOrder.workOrderId}
                  onWorkOrderDeleted={() => {
                    refreshWorkOrders();
                    onWorkOrderSelected(null);
                  }}
                  onMachineProfilesReassigned={() => {
                    if (selectedWorkOrder?.workOrderId === workOrder.workOrderId) onMachineProfilesReassigned();
                  }}
                />
                <Divider />
              </Fragment>
            ))}
            {workOrders.length === 0 && !loading && (
              <Text c="dimmed" fz="sm" p="md">
                No work orders found.
              </Text>
            )}
            <Box px="sm">
              <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
            </Box>
          </Stack>
        </Stack>
      </Stack>

      <DraggableDivider onDragDivider={(delta) => setPanelWidth((h) => h + delta)} orientation="vertical" />

      <Stack p="md" flex={1}>
        {isNil(selectedWorkOrder) ? (
          <Flex w="100%" h="100%" align="center" justify="center">
            <Text c="dimmed">Select a work order.</Text>
          </Flex>
        ) : (
          <FabricationItems
            onElementsUpdated={refreshWorkOrders}
            refreshTrigger={refreshTrigger}
            workOrderPageOpts={{
              workOrder: selectedWorkOrder,
            }}
          />
        )}
      </Stack>
    </Card>
  );
};
